<script setup lang="ts">
import type { ButtonProps } from '#layers/ui/types/components';
import type { DrawerPageProps } from './DrawerPage.vue';
import { computed, useId, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { mergeUi } from '#layers/ui/utils/merge-ui';
import DrawerPage from './DrawerPage.vue';

const props = defineProps<{
	title?: string;
	description?: string;
	formId?: string;
	submitAction?: ButtonProps;
	cancelAction?: ButtonProps;
} & Pick<DrawerPageProps, 'ui' | 'attachToRoute'>>();

/**
 * If this is any boolean value a "Create more" switch will be shown instead of the "Cancel" action
 */
const createMore = defineModel<boolean | undefined>('createMore', { default: undefined });

const { t } = useI18n();

const drawerRef = useTemplateRef('drawer');

const generatedFormId = useId();
const formId = computed(() => props.formId ?? generatedFormId);

const footerActions = computed(() => [
	...(createMore.value === undefined
		? [{
				label: t('common.cancel'),
				variant: 'ghost',
				color: 'neutral',
				onClick: close,
				...props.cancelAction,
			}]
		: []),
	{
		label: t('common.save'),
		form: formId.value,
		type: 'submit',
		...props.submitAction,
	},
] as ButtonProps[]);

function close() {
	drawerRef.value?.close();
}

defineExpose({
	close,
});
</script>

<template>
	<DrawerPage ref="drawer" :title :description :ui="mergeUi({ description: 'font-mono font-medium' } as DrawerPageProps['ui'], props.ui)">
		<slot :form-id :close />

		<template #title>
			<slot name="title" />
		</template>

		<template #footer-actions>
			<DSwitch
				v-if="createMore !== undefined"
				v-model="createMore"
				size="xs"
				:label="$t('common.create_more')"
				:ui="{ label: 'text-muted' }"
			/>
			<DButton v-for="(action, index) in footerActions" :key="index" v-bind="action" />
		</template>
	</DrawerPage>
</template>
